import React, { useCallback, useRef, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import ageVerifIcon from '../../assets/age-verif.svg';
import nakedNewsLogoWhite from '../../assets/Affiliate/Logos Icons/NN-logo.png';
import { getAgeVerificationProviderUrl } from '../../services/getAgeVerificationProviderUrl';
import { setAgeVerified } from '../../utils/ageVerification';
import { doLogout } from '../../utils/auth';

import styles from './AgeVerificationOverlay.module.css';


const vmaStyle = {
  height: 720,
  width: 480,
  left: () => (window.screen.width / 2) - (vmaStyle.height / 2),
  top: () => (window.screen.height / 2) - (vmaStyle.width / 2),
};

const YOTI_CALLBACK_PATH = '/yoti/verify';
const PING_INTERVAL = 5000;

const YOTI_CONTENT_TEXT =
  'As per state law, please verify your age. It takes just 30 seconds and we do not store personal data.';

function AgeVerificationOverlay(props) {
  const { user, onAgeVerified } = props;
  const popupRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const [ageVerificationUrl, setAgeVerificationUrl] = useState(null);
  // Tracks whether we have finished checking if the Yoti provider is available.
  // Until then we avoid rendering the fallback affirm button to prevent flicker.
  const [providerChecked, setProviderChecked] = useState(false);

  // Yoti is only "eligible" when the backend hands us a provider URL. When it
  // isn't, we fall back to a simple self-affirmation button.
  const isYotiEligible = Boolean(ageVerificationUrl);

  const onMessage = useCallback((event) => {
    const { data, source } = event;
    const { pathname: eventPath, origin: eventOrigin } = source.location;
    const { origin } = window.location;

    if (eventOrigin !== origin) {
      return;
    }

    if (eventPath !== YOTI_CALLBACK_PATH) {
      return;
    }

    window.removeEventListener('message', onMessage);
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    if (data.verified) {
      setAgeVerified();
      window.location.reload();
    }
  }, []);

  const startPopupPing = useCallback((popup) => {
    pingIntervalRef.current = setInterval(() => {
      if (popup.closed) {
        clearInterval(pingIntervalRef.current);

        return;
      }

      try {
        const targetOrigin = `${window.location.origin}/yoti/verify`;
        popup.postMessage({ type: 'ping' }, targetOrigin);
      } catch (error) {
        // Expected during external verification
      }
    }, PING_INTERVAL);
  }, []);

  useEffect(() => {
    const loadVerificationUrl = async() => {
      try {
        const params = { redirect_uri: window.location.origin };
        const { data } = await getAgeVerificationProviderUrl(params);
        const url = data?.ageVerificationUrl;
        setAgeVerificationUrl(url);
      } catch (error) {
        // No Yoti provider available: fall back to the self-affirmation button.
        setAgeVerificationUrl(null);
      } finally {
        setProviderChecked(true);
      }
    };

    loadVerificationUrl();
  }, []);

  const handleVerifyMyAge = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      return;
    }

    if (!ageVerificationUrl) {
      // eslint-disable-next-line no-alert
      alert('Verification URL not ready. Please try again in a moment.');

      return;
    }

    try {
      window.addEventListener('message', onMessage, false);

      const styleConfigs = [
        'resizable=no',
        `height=${vmaStyle.height}`,
        `width=${vmaStyle.width}`,
        `left=${vmaStyle.left()}`,
        `top=${vmaStyle.top()}`,
      ].join(',');

      popupRef.current = window.open(ageVerificationUrl, '', styleConfigs);

      if (popupRef.current) {
        startPopupPing(popupRef.current);
      } else {
        window.removeEventListener('message', onMessage);
        // eslint-disable-next-line no-alert
        alert('Please allow popups for age verification to work properly.');
      }
    } catch (error) {
      // eslint-disable-next-line no-alert
      alert('Failed to start age verification. Please try again.');
    }
  }, [ageVerificationUrl, onMessage, startPopupPing]);

  useEffect(() => () => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    window.removeEventListener('message', onMessage);
  }, [onMessage]);

  const handleAffirmAge = useCallback(() => {
    setAgeVerified();
    if (onAgeVerified) {
      onAgeVerified();
    }
  }, [onAgeVerified]);

  const handleLogin = useCallback(() => {
    window.location.href = '/account/login';
  }, []);

  const handleSignOut = useCallback((event) => {
    event.preventDefault();
    doLogout();
  }, []);

  const renderButtons = () => (
    <div className={styles.buttonsContainer}>
      {isYotiEligible ? (
        <button
          className={styles.verifyButton}
          onClick={handleVerifyMyAge}
        >
          Verify My Age
        </button>
      ) : (
        <button
          className={styles.verifyButton}
          disabled={!providerChecked}
          onClick={handleAffirmAge}
        >
          I Am 18+ Enter Site
        </button>
      )}
      {!user?.authenticated && (
        <button
          className={styles.loginButton}
          onClick={handleLogin}
        >
          Login
        </button>
      )}
      {user?.authenticated && (
        <button
          className={styles.signOutButton}
          onClick={handleSignOut}
        >
          Sign Out
        </button>
      )}
    </div>
  );

  const renderContent = () => {
    if (isYotiEligible) {
      return <div className={styles.content}>{YOTI_CONTENT_TEXT}</div>;
    }

    return (
      <div className={styles.content}>
        <div className={styles.affirmTitle}>Welcome to Naked News</div>
        <div className={styles.affirmSubtitle}>Adult Content Notice</div>
        <p>
          Naked News contains nudity and some sexually explicit content
          intended exclusively for adults.
        </p>
        <p>By entering this website, you certify that:</p>
        <ul className={styles.affirmList}>
          <li>
            You are 18 years of age or older, or the legal age required to
            access adult content in your jurisdiction.
          </li>
          <li>
            You understand that this website contains explicit adult material.
          </li>
          <li>
            You are accessing this content willingly and are legally permitted
            to do so.
          </li>
          <li>
            You agree to comply with all applicable local laws regarding access
            to adult content.
          </li>
        </ul>
        <p>
          If you are under the required age or do not wish to view adult
          material, please leave this site.
        </p>
      </div>
    );
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.container}>
        <div className={styles.imageContainer}>
          <img
            alt="Age Verification Required"
            className={classnames(styles.ageVerifIcon, 'unblurred')}
            src={ageVerifIcon}
          />
        </div>
        {renderContent()}
        {renderButtons()}
        <div
          className={classnames(styles.ageVerifRedBackground, { [styles.nnLogo]: true })}
        />
        <div className={styles.logoContainer}>
          <img
            alt="Naked News: The Program With Nothing To Hide"
            className={classnames(styles.logo, 'unblurred')}
            src={nakedNewsLogoWhite}
          />
        </div>
      </div>
    </div>
  );
}

AgeVerificationOverlay.propTypes = {
  user: PropTypes.object,
  onAgeVerified: PropTypes.func,
};

AgeVerificationOverlay.defaultProps = {
  user: null,
  onAgeVerified: null,
};

export default observer(AgeVerificationOverlay);
